/* Vedic chart tag emission.
   Deterministic: keyed off the chart structure (lagna, moon sign, sun sign,
   moon nakshatra), NOT extracted from the API interpretation text. This keeps
   emission reliable even when the interpretation call fails or is cached, and
   lets tags write the moment the chart is calculated.

   v1 scope (agreed): lagna + moon + sun + moon nakshatra. Structural extras
   (sun-moon polarity, lagna lord, Atmakaraka) deferred.

   Each layer uses a distinct key vocabulary on purpose: lagna = outward style,
   moon = inner emotional life, sun = will/vitality, nakshatra = specific
   psychology. Distinct keys keep the four layers from collapsing under the
   tag_key|tag_type dedup, so the same sign across layers still adds signal.

   Provenance in source_module ("star_chart_vedic"). */

// ── Lagna / rising (12) — the front door, outward manner. Highest signal. ───
export const VEDIC_LAGNA_TAGS = {
  aries: [{ key: "direct_approach", type: "offers" }, { key: "initiating_energy", type: "offers" }, { key: "quick_to_act", type: "latent" }],
  taurus: [{ key: "steady_presence", type: "offers" }, { key: "unhurried_manner", type: "offers" }, { key: "resistant_to_rush", type: "latent" }],
  gemini: [{ key: "quick_witted_front", type: "offers" }, { key: "adaptable_manner", type: "offers" }, { key: "scattered_focus", type: "latent" }],
  cancer: [{ key: "protective_manner", type: "offers" }, { key: "emotionally_attuned_front", type: "offers" }, { key: "guards_softness", type: "latent" }],
  leo: [{ key: "warm_commanding", type: "offers" }, { key: "generous_presence", type: "offers" }, { key: "needs_recognition", type: "latent" }],
  virgo: [{ key: "precise_helpful", type: "offers" }, { key: "discerning_eye", type: "offers" }, { key: "over_critical", type: "latent" }],
  libra: [{ key: "gracious_diplomatic", type: "offers" }, { key: "seeks_harmony", type: "offers" }, { key: "avoids_conflict", type: "latent" }],
  scorpio: [{ key: "intense_presence", type: "offers" }, { key: "penetrating_gaze", type: "offers" }, { key: "guarded_front", type: "latent" }],
  sagittarius: [{ key: "open_optimistic", type: "offers" }, { key: "candid_manner", type: "offers" }, { key: "blunt_at_times", type: "latent" }],
  capricorn: [{ key: "composed_authority", type: "offers" }, { key: "reserved_front", type: "offers" }, { key: "slow_to_warm", type: "latent" }],
  aquarius: [{ key: "original_manner", type: "offers" }, { key: "friendly_detachment", type: "offers" }, { key: "aloof_at_times", type: "latent" }],
  pisces: [{ key: "gentle_approachable", type: "offers" }, { key: "porous_boundaries", type: "latent" }, { key: "dreamy_front", type: "latent" }],
};

// ── Moon (12) — emotional inner life. Inward flavor, distinct from lagna. ────
export const VEDIC_MOON_TAGS = {
  aries: [{ key: "quick_to_feel", type: "latent" }, { key: "needs_emotional_momentum", type: "needs" }],
  taurus: [{ key: "steady_heart", type: "offers" }, { key: "needs_emotional_security", type: "needs" }],
  gemini: [{ key: "processes_feelings_in_words", type: "latent" }, { key: "restless_heart", type: "latent" }],
  cancer: [{ key: "deeply_nurturing", type: "offers" }, { key: "tidal_emotions", type: "latent" }],
  leo: [{ key: "warm_hearted_core", type: "offers" }, { key: "needs_to_feel_cherished", type: "needs" }],
  virgo: [{ key: "shows_love_through_service", type: "offers" }, { key: "anxious_inner_world", type: "latent" }],
  libra: [{ key: "peace_seeking_heart", type: "latent" }, { key: "needs_relational_harmony", type: "needs" }],
  scorpio: [{ key: "emotionally_loyal", type: "offers" }, { key: "feels_in_extremes", type: "latent" }],
  sagittarius: [{ key: "buoyant_heart", type: "offers" }, { key: "needs_emotional_freedom", type: "needs" }],
  capricorn: [{ key: "emotionally_self_reliant", type: "offers" }, { key: "guards_the_heart", type: "latent" }],
  aquarius: [{ key: "loves_through_ideals", type: "offers" }, { key: "detached_processing", type: "latent" }],
  pisces: [{ key: "boundless_compassion", type: "offers" }, { key: "emotionally_absorptive", type: "latent" }],
};

// ── Sun (12) — will and vitality. Smaller sets to avoid triple-counting. ────
export const VEDIC_SUN_TAGS = {
  aries: [{ key: "pioneering_spirit", type: "offers" }, { key: "willful_drive", type: "latent" }],
  taurus: [{ key: "values_stability", type: "offers" }, { key: "persistent_will", type: "latent" }],
  gemini: [{ key: "curious_intellect", type: "offers" }, { key: "restless_will", type: "latent" }],
  cancer: [{ key: "protective_purpose", type: "offers" }, { key: "home_centered_will", type: "latent" }],
  leo: [{ key: "creative_vitality", type: "offers" }, { key: "wants_to_shine", type: "latent" }],
  virgo: [{ key: "improvement_drive", type: "offers" }, { key: "service_purpose", type: "latent" }],
  libra: [{ key: "relational_purpose", type: "offers" }, { key: "seeks_fairness", type: "latent" }],
  scorpio: [{ key: "depth_drive", type: "offers" }, { key: "transformative_will", type: "latent" }],
  sagittarius: [{ key: "meaning_seeking", type: "offers" }, { key: "expansive_will", type: "latent" }],
  capricorn: [{ key: "ambition_drive", type: "offers" }, { key: "legacy_minded", type: "latent" }],
  aquarius: [{ key: "reform_drive", type: "offers" }, { key: "independent_will", type: "latent" }],
  pisces: [{ key: "compassion_purpose", type: "offers" }, { key: "transcendent_will", type: "latent" }],
};

// ── Moon nakshatra (27) — dense psychological signal. Keyed lowercase,
//    spaces to underscores ("purva_phalguni"). ──────────────────────────────
export const VEDIC_NAKSHATRA_TAGS = {
  ashwini: [{ key: "pioneering_healer", type: "offers" }, { key: "restless_speed", type: "latent" }],
  bharani: [{ key: "bears_burdens", type: "offers" }, { key: "intense_endurance", type: "latent" }],
  krittika: [{ key: "incisive_focus", type: "offers" }, { key: "burns_through_falsehood", type: "latent" }],
  rohini: [{ key: "magnetic_charm", type: "offers" }, { key: "sensual_appreciation", type: "latent" }],
  mrigashira: [{ key: "seeking_curiosity", type: "offers" }, { key: "never_satisfied", type: "latent" }],
  ardra: [{ key: "storm_then_clarity", type: "latent" }, { key: "emotional_intensity", type: "latent" }],
  punarvasu: [{ key: "renewal_after_loss", type: "offers" }, { key: "returns_home", type: "latent" }],
  pushya: [{ key: "nourishing_care", type: "offers" }, { key: "protective_duty", type: "latent" }],
  ashlesha: [{ key: "hypnotic_insight", type: "latent" }, { key: "coils_inward", type: "latent" }],
  magha: [{ key: "ancestral_pride", type: "offers" }, { key: "honors_lineage", type: "latent" }],
  purva_phalguni: [{ key: "warm_pleasure", type: "offers" }, { key: "needs_leisure", type: "latent" }],
  uttara_phalguni: [{ key: "generous_reliability", type: "offers" }, { key: "service_through_structure", type: "latent" }],
  hasta: [{ key: "deft_craft", type: "offers" }, { key: "clever_resourceful", type: "latent" }],
  chitra: [{ key: "artisan_brilliance", type: "offers" }, { key: "drawn_to_glamour", type: "latent" }],
  swati: [{ key: "independent_flexibility", type: "offers" }, { key: "bends_not_breaks", type: "latent" }],
  vishakha: [{ key: "determined_pursuit", type: "offers" }, { key: "goal_fixated", type: "latent" }],
  anuradha: [{ key: "devoted_friendship", type: "offers" }, { key: "thrives_in_groups", type: "latent" }],
  jyeshtha: [{ key: "protective_seniority", type: "offers" }, { key: "guarded_pride", type: "latent" }],
  mula: [{ key: "gets_to_the_root", type: "offers" }, { key: "uproots_to_rebuild", type: "latent" }],
  purva_ashadha: [{ key: "invincible_optimism", type: "offers" }, { key: "persuasive_force", type: "latent" }],
  uttara_ashadha: [{ key: "enduring_integrity", type: "offers" }, { key: "late_lasting_victory", type: "latent" }],
  shravana: [{ key: "deep_listener", type: "offers" }, { key: "gathers_wisdom", type: "latent" }],
  dhanishta: [{ key: "rhythmic_drive", type: "offers" }, { key: "wealth_minded", type: "latent" }],
  shatabhisha: [{ key: "independent_mystic", type: "latent" }, { key: "healing_secrecy", type: "latent" }],
  purva_bhadrapada: [{ key: "fiery_conviction", type: "latent" }, { key: "intense_idealism", type: "latent" }],
  uttara_bhadrapada: [{ key: "deep_calm", type: "offers" }, { key: "wise_restraint", type: "latent" }],
  revati: [{ key: "nurturing_guide", type: "offers" }, { key: "tender_completion", type: "latent" }],
};

const norm = (s) => String(s || "").trim().toLowerCase();
const normNk = (s) => norm(s).replace(/\s+/g, "_");

/* Collect deduped tag rows for a computed Vedic chart. Reads the chart
   structure only (never the API interpretation). Independent of birth time for
   sun/moon/nakshatra; lagna is included only when present (needs birth time).
   Dedup by tag_key|tag_type for the saveTags upsert. */
export function collectVedicTags(vedicChart) {
  if (!vedicChart) return [];
  const lagnaSign = vedicChart.lagna?.sign;
  const sunSign = vedicChart.planets?.sun?.sign;
  const moonSign = vedicChart.planets?.moon?.sign;
  const moonNk = vedicChart.planets?.moon?.nakshatra?.name;

  const layers = [
    lagnaSign && VEDIC_LAGNA_TAGS[norm(lagnaSign)]
      ? { tags: VEDIC_LAGNA_TAGS[norm(lagnaSign)], value: norm(lagnaSign) }
      : null,
    moonSign && VEDIC_MOON_TAGS[norm(moonSign)]
      ? { tags: VEDIC_MOON_TAGS[norm(moonSign)], value: norm(moonSign) }
      : null,
    sunSign && VEDIC_SUN_TAGS[norm(sunSign)]
      ? { tags: VEDIC_SUN_TAGS[norm(sunSign)], value: norm(sunSign) }
      : null,
    moonNk && VEDIC_NAKSHATRA_TAGS[normNk(moonNk)]
      ? { tags: VEDIC_NAKSHATRA_TAGS[normNk(moonNk)], value: normNk(moonNk) }
      : null,
  ].filter(Boolean);

  const seen = new Map();
  for (const layer of layers) {
    for (const t of layer.tags) {
      const k = `${t.key}|${t.type}`;
      if (!seen.has(k)) {
        seen.set(k, {
          tag_key: t.key,
          tag_value: layer.value,
          tag_type: t.type,
          source_module: "star_chart_vedic",
        });
      }
    }
  }
  return Array.from(seen.values());
}
